import React, { useEffect, useRef, useState } from "react";
import { Clapperboard, Sparkles, Bell, Check, Maximize2, RotateCw, Loader2, Gamepad2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

// AAA Unreal "Cinematic Mode" via Pixel Streaming (Eagle3D / any WebRTC signalling host).
// Auto-activates when a global or per-game stream URL is set. When not live, an admin "teaser"
// flag surfaces a coming-soon button to gauge demand. See UNREAL_PIXEL_STREAMING_SETUP.md.
export default function CinematicMode({ gameId, game }) {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [interested, setInterested] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [ended, setEnded] = useState(false);
  const iframeRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const q = gameId ? `?game_id=${encodeURIComponent(gameId)}` : "";
    api.get(`/cinematic/config${q}`).then(({ data }) => setCfg(data)).catch(() => setCfg({ enabled: false, teaser: false }));
  }, [gameId]);

  if (!cfg) return null;
  const enabled = !!cfg.enabled;
  const teaser = !!cfg.teaser;
  if (!enabled && !teaser) return null; // hidden unless live or admin-teased

  const streamUrl = enabled ? cfg.signalling_url : "";
  const limitMin = Number(cfg.session_limit_min || 0);
  const overBudget = !!cfg.over_budget;

  const clearTimer = () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };
  const startTimer = () => { clearTimer(); if (limitMin > 0) timerRef.current = setTimeout(() => setEnded(true), limitMin * 60 * 1000); };
  const openStream = () => { setEnded(false); setLoading(true); setReloadKey((k) => k + 1); setOpen(true); startTimer(); api.post("/cinematic/session", { game_id: gameId }).catch(() => {}); };
  const reload = () => { setEnded(false); setLoading(true); setReloadKey((k) => k + 1); startTimer(); };
  const handleOpenChange = (v) => { setOpen(v); if (!v) { clearTimer(); setEnded(false); } };
  const goFullscreen = () => { const el = iframeRef.current; if (el?.requestFullscreen) el.requestFullscreen(); else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen(); };
  const onFrameLoad = () => {
    setLoading(false);
    // Focus the iframe so W/A/S/D and gamepad inputs route to the stream immediately.
    try { iframeRef.current?.focus(); iframeRef.current?.contentWindow?.focus(); } catch { /* cross-origin focus is best-effort */ }
  };

  const notifyMe = async () => {
    setInterested(true);
    try { await api.post("/cinematic/interest", { game_id: gameId }); toast.success("Nice — we'll ping you when Cinematic Mode goes live! 🎬"); }
    catch { /* non-blocking */ }
  };

  return (
    <>
      <Button
        variant="outline" size="sm" onClick={() => (enabled ? openStream() : setOpen(true))}
        className="rounded-full gap-1.5 border-amber-400/50 text-amber-300 hover:bg-amber-400/10 relative"
        data-testid="cinematic-mode-btn"
      >
        <Clapperboard className="w-4 h-4" /> Cinematic Mode
        {enabled
          ? <Badge className="ml-1 text-[9px] px-1.5 py-0 bg-gradient-to-r from-amber-400 to-orange-500 text-black border-0" data-testid="cinematic-aaa">AAA</Badge>
          : <Badge variant="secondary" className="ml-1 text-[9px] px-1.5 py-0" data-testid="cinematic-soon">{overBudget ? "Paused" : "Soon"}</Badge>}
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className={enabled ? "sm:max-w-[min(96vw,1200px)] bg-[#07080c] border-amber-400/20" : "sm:max-w-md"} data-testid="cinematic-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clapperboard className="w-5 h-5 text-amber-400" /> Cinematic Mode
              {enabled && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300">UNREAL · LIVE</span>}
            </DialogTitle>
            <DialogDescription>
              {enabled
                ? `Streaming "${game?.title || "this game"}" in AAA Unreal quality, rendered live on a cloud GPU.`
                : "Play this game with AAA Unreal-quality visuals, rendered on a cloud GPU and streamed live to your browser."}
            </DialogDescription>
          </DialogHeader>

          {enabled ? (
            <div className="space-y-2" data-testid="cinematic-live">
              <div className="relative w-full rounded-xl overflow-hidden border border-amber-400/20 bg-black" style={{ aspectRatio: "16/9" }}>
                {loading && !ended && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-[#0b0d12] to-black text-center px-6" data-testid="cinematic-loading">
                    <div className="relative">
                      <Loader2 className="w-10 h-10 animate-spin text-amber-400" />
                      <Sparkles className="w-4 h-4 text-amber-300 absolute -top-1 -right-1" />
                    </div>
                    <div>
                      <p className="font-display font-bold text-white">Booting your cloud GPU…</p>
                      <p className="text-xs text-white/50 mt-0.5">Spinning up real-time Unreal Engine rendering — AAA visuals incoming.</p>
                    </div>
                  </div>
                )}
                {ended ? (
                  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-[#0b0d12] to-black text-center px-6" data-testid="cinematic-session-ended">
                    <Clock className="w-9 h-9 text-amber-400" />
                    <div>
                      <p className="font-display font-bold text-white">Session ended</p>
                      <p className="text-xs text-white/50 mt-0.5">Your {limitMin}-minute Cinematic session wrapped up to keep cloud-GPU costs in check.</p>
                    </div>
                    <Button onClick={reload} className="rounded-full gap-1.5 bg-gradient-to-r from-amber-400 to-orange-500 text-black" data-testid="cinematic-restart">
                      <RotateCw className="w-4 h-4" /> Start again
                    </Button>
                  </div>
                ) : (
                  <iframe
                    key={reloadKey}
                    ref={iframeRef}
                    src={streamUrl}
                    title="Cinematic Mode stream"
                    onLoad={onFrameLoad}
                    className="absolute inset-0 w-full h-full"
                    allow="autoplay; fullscreen; gamepad; microphone; camera; xr-spatial-tracking; clipboard-write"
                    allowFullScreen
                    data-testid="cinematic-player"
                  />
                )}
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] text-white/40 flex items-center gap-1.5">
                  <Gamepad2 className="w-3.5 h-3.5" /> Click the stream, then use your keyboard / gamepad.{limitMin > 0 ? ` Auto-stops after ${limitMin} min.` : " Powered by Unreal Pixel Streaming."}
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={reload} className="rounded-full text-white/70 hover:text-white gap-1.5" data-testid="cinematic-reload">
                    <RotateCw className="w-4 h-4" /> Reload
                  </Button>
                  <Button variant="outline" size="sm" onClick={goFullscreen} className="rounded-full border-amber-400/40 text-amber-300 hover:bg-amber-400/10 gap-1.5" data-testid="cinematic-fullscreen">
                    <Maximize2 className="w-4 h-4" /> Fullscreen
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-amber-400/30 bg-gradient-to-br from-amber-400/10 to-transparent p-5 text-center space-y-3" data-testid="cinematic-coming-soon">
              <Sparkles className="w-8 h-8 text-amber-400 mx-auto" />
              <p className="font-semibold">{overBudget ? "Paused this month" : "Coming soon"}</p>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                {overBudget
                  ? "Cinematic Mode is taking a short breather — this month's cloud-GPU budget was reached. It'll switch back on at the start of next month."
                  : "Cinematic Mode will stream real-time, movie-grade Unreal Engine visuals of your games — no downloads, right in the browser."}
              </p>
              {overBudget ? null : interested ? (
                <p className="text-sm font-semibold text-emerald-400 flex items-center justify-center gap-1.5" data-testid="cinematic-interested">
                  <Check className="w-4 h-4" /> You're on the list!
                </p>
              ) : (
                <Button onClick={notifyMe} className="rounded-full gap-1.5 bg-gradient-to-r from-amber-400 to-orange-500 text-black" data-testid="cinematic-notify-btn">
                  <Bell className="w-4 h-4" /> I want this — notify me
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
